import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
    Trophy, Path, Lightning, Star, ShoppingCart, ChatCircle,
    Users, MapPin, Flame, Crown, Check, Lock
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

const ICON_MAP = {
    path: Path, lightning: Lightning, star: Star, cart: ShoppingCart,
    chat: ChatCircle, users: Users, mappin: MapPin, flame: Flame, crown: Crown,
};
const CAT_COLORS = {
    rides: 'text-primary', distance: 'text-electric-blue', community: 'text-hot-pink',
    shop: 'text-neon-gold', social: 'text-cyber-purple', ar: 'text-boltz-gold', status: 'text-neon-green',
};

export default function Badges() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checking, setChecking] = useState(false);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        fetch(`${API}/badges`, { credentials: 'include', headers: getH() })
            .then(r => r.ok ? r.json() : null)
            .then(setData)
            .finally(() => setLoading(false));
    }, []);

    const checkBadges = async () => {
        setChecking(true);
        try {
            const res = await fetch(`${API}/badges/check`, {
                method: 'POST', credentials: 'include', headers: getH(),
            });
            if (res.ok) {
                const result = await res.json();
                if (result.newly_earned.length > 0) {
                    toast.success(`Earned ${result.newly_earned.length} new badge${result.newly_earned.length > 1 ? 's' : ''}!`);
                    const refreshed = await fetch(`${API}/badges`, { credentials: 'include', headers: getH() });
                    if (refreshed.ok) setData(await refreshed.json());
                } else {
                    toast.info('No new badges earned yet. Keep riding!');
                }
            }
        } catch { toast.error('Failed'); }
        finally { setChecking(false); }
    };

    const categories = ['all', ...new Set((data?.badges || []).map(b => b.category))];
    const filtered = filter === 'all' ? (data?.badges || []) : (data?.badges || []).filter(b => b.category === filter);
    const earned = filtered.filter(b => b.earned);
    const locked = filtered.filter(b => !b.earned);

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-neon-gold text-glow-gold">ACHIEVEMENTS</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground" data-testid="badges-title">
                        Badges
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        {data?.earned_count || 0} / {data?.total_count || 0} earned
                    </p>
                </motion.div>

                <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-1.5 overflow-x-auto">
                        {categories.map(c => (
                            <Button key={c} size="sm" variant={filter === c ? 'default' : 'outline'}
                                className={`rounded-full text-xs capitalize ${filter === c ? 'btn-primary' : ''}`}
                                onClick={() => setFilter(c)}>{c}</Button>
                        ))}
                    </div>
                    <Button size="sm" onClick={checkBadges} disabled={checking} className="btn-primary shrink-0" data-testid="check-badges-btn">
                        {checking ? '...' : 'Check'}
                    </Button>
                </div>

                {/* Earned */}
                {earned.length > 0 && (
                    <div className="mb-6">
                        <h3 className="font-display text-xs text-neon-green tracking-wider mb-3">EARNED</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {earned.map(b => {
                                const Icon = ICON_MAP[b.icon] || Trophy;
                                const color = CAT_COLORS[b.category] || 'text-primary';
                                return (
                                    <motion.div key={b.badge_id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
                                        <Card className="glass-card border-neon-green/20" data-testid={`badge-${b.badge_id}`}>
                                            <CardContent className="p-4 text-center">
                                                <div className="w-12 h-12 rounded-full bg-neon-green/10 flex items-center justify-center mx-auto mb-2">
                                                    <Icon size={24} className={color} weight="fill" />
                                                </div>
                                                <p className="font-display font-bold text-sm text-foreground">{b.name}</p>
                                                <p className="text-[10px] text-muted-foreground mt-0.5">{b.description}</p>
                                                <Badge className="mt-2 bg-neon-green/10 text-neon-green border-neon-green/20 text-[9px]">
                                                    <Check size={8} className="mr-0.5" />Earned
                                                </Badge>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Locked */}
                {locked.length > 0 && (
                    <div>
                        <h3 className="font-display text-xs text-muted-foreground tracking-wider mb-3">LOCKED</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {locked.map(b => {
                                const Icon = ICON_MAP[b.icon] || Trophy;
                                return (
                                    <Card key={b.badge_id} className="glass-card opacity-60" data-testid={`badge-locked-${b.badge_id}`}>
                                        <CardContent className="p-4 text-center">
                                            <div className="w-12 h-12 rounded-full bg-muted/30 flex items-center justify-center mx-auto mb-2 relative">
                                                <Icon size={24} className="text-muted-foreground" />
                                                <Lock size={10} className="absolute bottom-0 right-0 text-muted-foreground" />
                                            </div>
                                            <p className="font-display font-bold text-sm text-muted-foreground">{b.name}</p>
                                            <p className="text-[10px] text-muted-foreground/60 mt-0.5">{b.description}</p>
                                            <Progress value={b.progress * 100} className="h-1 mt-2" />
                                            <p className="text-[9px] text-muted-foreground mt-1 font-mono">{b.current}/{b.threshold}</p>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
